import { exitFor } from "./lab-strategies.js";
import {
  SIGNALS_MAX,
  SIGNALS_MAX_SESSIONS,
  SIGNALS_SESSIONS,
  type SignalsSearch,
  STRATEGY_LAB_DEFAULTS,
  type StrategyLabSearch,
} from "./search-params.js";

/**
 * What each cached question is, and how deep it is allowed to ask.
 *
 * Split out of `queries.ts` because the key policy and the window clamps are
 * one concern — what counts as the same question — and the hooks that ask
 * them are another. The clamps live here rather than beside their hook
 * because the keys depend on them: a key that ignored the limit would serve a
 * shallower cached page back to a URL that asked for a deeper one.
 */

export interface ScreenParams {
  session?: string | undefined;
  factors?: string[] | undefined;
}

/**
 * What one strategy-lab read asks the server. Every field is optional, and an
 * absent one is the server's default; `queryKeys.strategyLab` spells those out.
 *
 * Derived from the URL schema rather than written out, so the page can pass
 * its search straight through and the two cannot drift. Declared here rather
 * than in `strategy-lab-queries.ts` for the reason `ScreenParams` is: the key
 * needs it, and this module must not import the hooks that import it.
 */
export type LabParams = Pick<
  StrategyLabSearch,
  | "strategy"
  | "from"
  | "to"
  | "split"
  | "exit"
  | "universe"
  | "minTurnoverCr"
  | "minTrades"
  | "group"
>;

/**
 * One stock's trades under a window the page has already applied. Every field
 * is required: the page builds it from `LabResponse.applied` and `run`, which
 * have resolved each default to a value.
 */
export interface LabTradesParams {
  /** The run the page's summary came from; the route answers 409 once it is replaced. */
  run: string;
  strategy: string;
  symbol: string;
  from: string;
  to: string;
  exit: "own" | "hold21";
  universe: "liquid" | "nifty500";
  /** Null under `nifty500`, which applies no turnover floor. */
  minTurnoverCr: number | null;
}

/** A turnover floor only exists on the liquid universe; under `nifty500` the key holds null. */
function floorKey(universe: "liquid" | "nifty500", minTurnoverCr: number | null): number | null {
  return universe === "nifty500" ? null : minTurnoverCr;
}

/** One page of the feed, five times what `GET /api/signals` defaults to. */
export const SIGNALS_DEFAULT_LIMIT = 500;

/**
 * How deep to ask, clamped to what the route will answer.
 *
 * The default is a page, not the ceiling. Defaulting to `SIGNALS_MAX` meant
 * the limit never bound inside the session window — two sessions held 2,525
 * rows on 2026-08-28 — so the browser asked for, parsed, and sorted every row
 * the filters matched. Production answered
 * `?minSeverity=urgent&sessions=2&limit=5000` with 480 KB, and the route's
 * read takes `limit` rows per family before the outer LIMIT
 * (`packages/db/src/repo/signals/feed.ts`), so 5,000 makes Postgres
 * materialise 20,000 rows across four families to hand back 5,000.
 *
 * 500 keeps a severity-, family-, or symbol-filtered window arriving whole,
 * and sorting stays client-side over what arrived, so a header click still
 * costs no round trip. On an unfiltered two-session window it now sorts a
 * page of the window rather than all of it — `FeedTruncation` says so on
 * screen, and every filter reaches the database, so narrowing finds the rest.
 *
 * `GET /api/signals` validates `limit` with `.max(5000)`, which rejects rather
 * than truncates, so a hand-edited `?limit=6000` is clamped here instead of
 * turning the whole page into "returned 400". The key uses this too, so
 * `?limit=6000` and `?limit=7000` share one cache entry rather than refetching
 * the identical 5,000 rows under a second key.
 */
export function feedLimit(search: SignalsSearch): number {
  return Math.min(search.limit ?? SIGNALS_DEFAULT_LIMIT, SIGNALS_MAX);
}

/**
 * How many sessions the feed covers, clamped to what the route will answer.
 *
 * `GET /api/signals` validates `sessions` with `.max(365)`, which rejects
 * rather than truncates, so forwarding a hand-edited `?sessions=400` turns the
 * whole page into "returned 400" instead of a feed. Same treatment `feedLimit`
 * gives `limit`, and for the same reason.
 *
 * `0` survives the clamp as `0`, because zero is "every session" rather than
 * an empty window. Translating it to the absent parameter the route spells
 * that with is `useSignals`' job, not this one: the key needs the number.
 */
export function feedSessions(search: SignalsSearch): number {
  return Math.min(search.sessions ?? SIGNALS_SESSIONS, SIGNALS_MAX_SESSIONS);
}

/**
 * Keys depend on the session and the factor list only.
 *
 * Filtering and sorting are client-side over one payload, so a filter change
 * must not invalidate anything: the whole point of shipping 110KB once is
 * that no interaction costs a round trip.
 */
export const queryKeys = {
  screen: (params: ScreenParams) =>
    ["screen", params.session ?? "latest", params.factors?.join(",") ?? "all"] as const,
  factors: () => ["factors"] as const,
  lab: (factorId: string, session?: string) => ["lab", factorId, session ?? "latest"] as const,
  symbol: (symbol: string) => ["symbol", symbol] as const,
  /**
   * Keyed on the symbol only. The window control slices a payload already in
   * hand, so a window change must not invalidate anything - the same reason
   * the screen's key ignores filters.
   */
  intraday: (symbol: string) => ["intraday", symbol] as const,
  signals: (search: SignalsSearch) =>
    [
      "signals",
      search.family ?? "all",
      search.minSeverity ?? "info",
      search.symbol ?? "any",
      search.signalType ?? "any",
      search.marketCap ?? "any",
      search.side ?? "any",
      search.acknowledged ?? "both",
      // Widening the window is a different question, not a deeper page: two
      // sessions and every session are different row sets, so they cannot
      // share a cache entry.
      feedSessions(search),
      // Part of the key, not a parameter of a shared one: two depths are two
      // different row sets, and a key that ignored the limit would serve a
      // shallower cached page back to a URL that asked for a deeper one.
      feedLimit(search),
    ] as const,
  signal: (id: string) => ["signal", id] as const,
  health: () => ["health"] as const,
  strategySettings: () => ["strategy-settings"] as const,
  trades: () => ["trades"] as const,
  rating: (signalId: string) => ["rating", signalId] as const,
  exDateFlags: () => ["ex-date-flags"] as const,
  /**
   * Every parameter `GET /api/strategy-lab` reads, normalised the way the
   * server resolves it, so two URLs that get the same answer share one cache
   * entry: `?minTrades=20` and no `minTrades`, `?exit=own` on a strategy with
   * no stop (the server reads it on the hold), and a turnover floor under
   * `nifty500` (the server applies none). Two windows never share one. "run"
   * and "mid" stand for the run's own history and its midpoint, which only
   * the server knows.
   */
  strategyLab: (params: LabParams) => {
    const strategy = params.strategy ?? STRATEGY_LAB_DEFAULTS.strategy;
    const universe = params.universe ?? STRATEGY_LAB_DEFAULTS.universe;
    return [
      "strategy-lab",
      strategy,
      params.from ?? "run",
      params.to ?? "run",
      params.split ?? "mid",
      exitFor(strategy, params.exit ?? STRATEGY_LAB_DEFAULTS.exit),
      universe,
      floorKey(universe, params.minTurnoverCr ?? STRATEGY_LAB_DEFAULTS.minTurnoverCr),
      params.minTrades ?? STRATEGY_LAB_DEFAULTS.minTrades,
      params.group ?? STRATEGY_LAB_DEFAULTS.group,
    ] as const;
  },
  strategyLabTrades: (params: LabTradesParams) =>
    [
      "strategy-lab-trades",
      params.run,
      params.strategy,
      params.symbol,
      params.from,
      params.to,
      exitFor(params.strategy, params.exit),
      params.universe,
      floorKey(params.universe, params.minTurnoverCr),
    ] as const,
};
